import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";

const SUPPORTED = ["en", "es", "pt"] as const;
type Lang = (typeof SUPPORTED)[number];

// Map ISO country codes to supported languages
const COUNTRY_TO_LANG: Record<string, Lang> = {
  // Portuguese-speaking
  BR: "pt", PT: "pt", AO: "pt", MZ: "pt", CV: "pt", GW: "pt", ST: "pt", TL: "pt",
  // Spanish-speaking
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", EC: "es",
  GT: "es", CU: "es", BO: "es", DO: "es", HN: "es", PY: "es", SV: "es", NI: "es",
  CR: "es", PA: "es", UY: "es", PR: "es", GQ: "es",
};

const STORAGE_KEY = "i18nextLng";
const GEO_CACHE_KEY = "i18nextGeoCountry";
const USER_CHOICE_KEY = "i18nextUserChoice";

// Multiple geo providers for resilience
const GEO_PROVIDERS = [
  { url: "https://ipapi.co/json/", field: "country_code" },
  { url: "https://ipwho.is/", field: "country_code" },
  { url: "https://get.geojs.io/v1/ip/country.json", field: "country" },
];

async function fetchCountry(): Promise<string | null> {
  // Use cached country if available (valid for session)
  const cached = sessionStorage.getItem(GEO_CACHE_KEY);
  if (cached) return cached;

  for (const provider of GEO_PROVIDERS) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 2500);
      const res = await fetch(provider.url, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) continue;
      const data = await res.json();
      const country = (data?.[provider.field] as string | undefined)?.toUpperCase();
      if (country && /^[A-Z]{2}$/.test(country)) {
        sessionStorage.setItem(GEO_CACHE_KEY, country);
        return country;
      }
    } catch {
      // try next provider
    }
  }
  return null;
}

async function applyGeoLanguage() {
  try {
    // Respect explicit user choice — never override
    if (localStorage.getItem(USER_CHOICE_KEY) === "1") return;
    // Respect any previously stored language (returning visitor) — no flicker
    if (localStorage.getItem(STORAGE_KEY)) return;

    const country = await fetchCountry();
    if (!country) return;

    const lang = COUNTRY_TO_LANG[country] ?? "en";
    if (lang === i18n.language?.slice(0, 2)) return;

    // Persist so future loads skip the geo swap entirely (no flicker on revisit)
    localStorage.setItem(STORAGE_KEY, lang);

    // Defer to next frame so initial paint completes first, avoiding mid-render swap
    const swap = () => { void i18n.changeLanguage(lang); };
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(swap);
    } else {
      setTimeout(swap, 0);
    }
  } catch {
    // Fail silently — default language stays in place
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      pt: { translation: pt },
    },
    supportedLngs: SUPPORTED as unknown as string[],
    fallbackLng: "en",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: STORAGE_KEY,
    },
  })
  .then(() => {
    // After init, run geo detection unless user explicitly chose a language
    void applyGeoLanguage();
  });

// Mark as user choice when changed via UI (LanguageSwitcher)
i18n.on("languageChanged", () => {
  // Persist preferred language to the user's auth metadata so emails use it.
  // Lazy-loaded to avoid pulling supabase into i18n init path.
  void (async () => {
    try {
      const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
      if (!["en", "es", "pt"].includes(lng)) return;
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getUser();
      const current = (data.user?.user_metadata as Record<string, unknown> | undefined)?.preferred_language;
      if (data.user && current !== lng) {
        await supabase.auth.updateUser({ data: { preferred_language: lng } });
      }
    } catch {
      // non-fatal
    }
  })();
});

// Export helper for LanguageSwitcher to mark explicit choice
export function setUserLanguage(lng: Lang) {
  localStorage.setItem(USER_CHOICE_KEY, "1");
  return i18n.changeLanguage(lng);
}

export default i18n;
